import { Rng } from '../core/rng';
import { bearingToX, bearingToZ, clamp, wrapAngle, Vec3 } from '../core/math';
import { AMMO, DIFFICULTIES, PERSONALITIES, type AmmoId, type Difficulty, type DifficultyId, type Personality } from '../data/classes';
import { aimHeight, FLIGHT_DT, type AimPart } from './gunnery';
import { TRAIN_LIMIT } from './cannons';
import { awarenessRate, sightRange } from './detection';
import type { Vessel } from './vessel';
import type { Wind } from './wind';
import type { SimEvent, SimEvents } from './events';

// An AI captain. He only knows what his lookouts see and what he hears:
// contacts have to be spotted (weather, darkness, sea state and range all
// matter), are estimated rather than exact, go stale when lost, and can be
// passed on by signal to consorts. From that picture he chooses what to do
// (patrol, investigate, engage, pursue, flee, regroup, board, ram), picks
// a heading by scoring manoeuvres the way a seaman would (the wind rules
// everything), fights his guns with ranging, lead and roll timing, and
// changes ammunition to suit. Difficulty buys better judgement, faster
// reactions and teamwork - not better aim.

export type Mode = 'patrol' | 'investigate' | 'search' | 'engage' | 'harry' | 'pursue' | 'flee' | 'regroup' | 'board' | 'ram' | 'idle';

export interface Contact {
  vessel: number;
  x: number;
  z: number;
  vx: number;
  vz: number;
  heading: number;
  /** World time last actually seen. */
  seen: number;
  /** Detection progress 0..1 (seen when >= 1). */
  awareness: number;
  visible: boolean;
  /** Came from a signal or a sound, not our own lookouts. */
  secondHand: boolean;
}

export interface Battlefield {
  vessels: readonly Vessel[];
  time: number;
  wind: Wind;
  /** Meteorological visibility, m. */
  visibility: number;
  /** 0 night .. 1 day. */
  daylight: number;
  /** Significant wave height, m. */
  seaState: number;
  events: SimEvents;
  /** Events from the previous step (gunfire heard, explosions seen). */
  recent: readonly SimEvent[];
  /** Who is attacking whom (for flanking). */
  attackers: Map<number, number[]>;
}

export function hostile(a: Vessel, b: Vessel): boolean {
  if (a === b || !b.alive) return false;
  if (a.faction === 'merchant' || b.faction === 'merchant') return a.faction === 'pirate' || a.faction === 'player' ? b.faction === 'merchant' : b.faction === 'player' || b.faction === 'pirate';
  if (a.faction === b.faction) return false;
  return true;
}

/**
 * What a ship is about with her guns, as it would look to `target` (whose
 * lookouts see her gun ports open, her guns run out and trained, her helm
 * going over): 'idle' (not fighting us), 'reloading', 'coming' (bringing a
 * loaded broadside round onto us), or 'bearing' (that broadside run out and
 * laid on us: she fires as her guns come on).
 */
export interface Intent {
  state: 'idle' | 'reloading' | 'coming' | 'bearing';
  /** Her side that faces us (+1 starboard), and the side she is bringing to bear (0 none). */
  side: 1 | -1;
  presenting: 0 | 1 | -1;
  /** How squarely we lie in that side's arc: 1 on her beam, 0 beyond her guns' traverse. */
  arc: number;
  /** Fractions of that side's guns loaded, and laid on us. */
  loaded: number;
  laid: number;
  /** Her reload of that side, 0..1. */
  reload: number;
}

export function intentToward(v: Vessel, target: Vessel): Intent {
  const s = v.ship;
  const l = s.worldToLocal(target.ship.com, tmpI);
  const side = (l.z >= 0 ? 1 : -1) as 1 | -1;
  const off = Math.abs(Math.atan2(l.x, Math.abs(l.z)));
  const arc = clamp(1 - off / (TRAIN_LIMIT + 0.12), 0, 1);
  const b = v.battery;
  const n = Math.max(1, b.count(side));
  // Loaded (those waiting on the roll to fire included), and ordered to fire.
  let charged = 0, ordered = 0;
  for (const g of b.guns) {
    if (g.spec.side !== side || g.destroyed || !g.loaded) continue;
    charged++;
    if (g.fireAt >= 0) ordered++;
  }
  const loaded = charged / n;
  const reload = b.reloadProgress(side);
  const cap = v.captain;
  const onUs = !!cap && v.fighting && cap.target === target.id && (hostile(v, target) || hostile(target, v));
  const out: Intent = { state: 'idle', side, presenting: 0, arc, loaded, laid: 0, reload };
  if (!onUs || !cap) return out;
  out.presenting = cap.presenting;
  const aim = b.aim;
  if (aim && aim.side === side && cap.layingOn === target.id) out.laid = aim.laid / n;
  if (ordered > 0 || (out.laid >= 0.5 && arc > 0 && loaded >= 0.3)) out.state = 'bearing';
  else if (loaded < 0.3) out.state = 'reloading';
  else if (out.presenting !== 0 || (arc > 0.25 && loaded >= 0.6)) out.state = 'coming';
  return out;
}

const tmpI = new Vec3();

/** Rough fighting power: guns x weight x men x hull. */
export function power(v: Vessel): number {
  let guns = 0;
  for (const g of v.battery.guns) if (!g.destroyed) guns++;
  return guns * v.battery.weight * (0.3 + 0.7 * v.crew.strength) * (0.3 + 0.7 * v.damage.integrity) + v.crew.effective * 0.05;
}

/** How fast she goes on a given point of sail (fraction of her best). */
function polar(windAngle: number): number {
  const a = Math.abs(windAngle);
  if (a < 0.95) return 0.05;
  if (a < 1.2) return 0.5 + (a - 0.95) * 1.2;
  if (a < 2.2) return 0.85 + (a - 1.2) * 0.15;
  return 1.0 - (a - 2.2) * 0.2;
}

export class Captain {
  readonly personality: Personality;
  readonly difficulty: Difficulty;
  mode: Mode = 'patrol';
  modeTime = 0;
  readonly contacts = new Map<number, Contact>();
  target = -1;
  route: { x: number; z: number }[] = [];
  private wp = 0;
  private poi: { x: number; z: number; until: number } | null = null;
  desiredHeading = 0;
  private decisionTimer = 0;
  private perceptionTimer = 0;
  private gunTimer = 0;
  /** What his guns are being laid on: a ship (by id), with which side, and how high on her. */
  private laying: { vessel: number; side: 1 | -1; height: number } | null = null;
  /** His misjudging of that ship's range, a fraction of it (over +), and which ship it's of. */
  private rangeErr = 0;
  private rangeOf = -1;
  /** A short turn to bring a loaded broadside (`side`) to bear, overriding the plan. */
  private present: { heading: number; until: number; side: 1 | -1 } | null = null;
  private reactionTimer = 0;
  private readonly rng: Rng;
  /** Heard or seen things waiting for the reaction delay. */
  private pendingSignals: { t: number; c: Contact }[] = [];
  private signalled = new Set<number>();
  /** Explanation of the last decision (for the HUD / debugging). */
  intent = '';
  /** Ships sailing in company with her (a convoy and its escort), by id. */
  convoy: number[] = [];
  /** She escorts the convoy: stays with it, and goes for whatever threatens it. */
  escort = false;
  /**
   * Dev: a mode forced on him whenever he has an enemy in view (null, as in
   * the game, leaves it to his judgement). 'idle' holds his course and fire.
   */
  forced: Mode | null = null;

  /** The broadside he is turning to bring to bear (0: none). */
  get presenting(): 0 | 1 | -1 {
    return this.present?.side ?? 0;
  }

  /** The ship his guns are laid on (-1 none). */
  get layingOn(): number {
    return this.laying?.vessel ?? -1;
  }

  constructor(readonly vessel: Vessel, difficulty: DifficultyId, seed: number) {
    this.rng = new Rng(seed);
    this.personality = PERSONALITIES[vessel.cls.personality];
    this.difficulty = DIFFICULTIES[difficulty];
    this.desiredHeading = vessel.ship.heading;
  }

  // ------------------------------------------------------------------ perception

  private perceive(dt: number, bf: Battlefield): void {
    const me = this.vessel;
    const ship = me.ship;
    for (const o of bf.vessels) {
      if (o === me || !o.alive) continue;
      const dx = o.ship.com.x - ship.com.x, dz = o.ship.com.z - ship.com.z;
      const d = Math.hypot(dx, dz);
      const range = sightRange(me, o, bf);
      let c = this.contacts.get(o.id);
      if (!c) {
        c = { vessel: o.id, x: o.ship.com.x, z: o.ship.com.z, vx: 0, vz: 0, heading: 0, seen: -1e9, awareness: 0, visible: false, secondHand: false };
        this.contacts.set(o.id, c);
      }
      if (d < range) {
        const rate = awarenessRate(d, range, me.crew.gunnery);
        c.awareness = Math.min(1.5, c.awareness + rate * dt);
      } else {
        c.awareness = Math.max(0, c.awareness - dt * 0.05);
      }
      const nowVisible = c.awareness >= 1 && d < range * 1.1;
      if (nowVisible) {
        // Bearing is good; range is judged, with an error growing with distance.
        const err = 1 + this.rng.gauss() * 0.04 * (d / Math.max(range, 1));
        const ex = ship.com.x + dx * err, ez = ship.com.z + dz * err;
        const k = c.visible ? 0.25 : 1;
        if (c.visible) {
          c.vx += ((ex - c.x) / dt - c.vx) * 0.1;
          c.vz += ((ez - c.z) / dt - c.vz) * 0.1;
        } else {
          c.vx = o.ship.vel.x;
          c.vz = o.ship.vel.z;
          if (hostile(me, o)) bf.events.push({ type: 'sighted', vessel: me.id, target: o.id });
        }
        c.x += (ex - c.x) * k;
        c.z += (ez - c.z) * k;
        c.heading = o.ship.heading;
        c.seen = bf.time;
        c.secondHand = false;
      } else if (c.seen > -1e8) {
        // Lost: dead-reckon from the last estimate.
        c.x += c.vx * dt;
        c.z += c.vz * dt;
      }
      c.visible = nowVisible;
    }
    // Gunfire carries a long way; explosions and burning ships are seen.
    for (const e of bf.recent) {
      if (e.type !== 'cannonFire' && e.type !== 'explosion') continue;
      if (e.type === 'cannonFire' && e.vessel === me.id) continue;
      const d = Math.hypot(e.x - ship.com.x, e.z - ship.com.z);
      const heard = e.type === 'cannonFire' ? d < 12000 : d < bf.visibility * 1.5;
      if (!heard || (this.poi && this.poi.until > bf.time + 60)) continue;
      const brg = Math.atan2(e.z - ship.com.z, e.x - ship.com.x) + this.rng.gauss() * 0.12;
      const est = d * (1 + this.rng.gauss() * 0.3);
      this.poi = { x: ship.com.x + Math.cos(brg) * est, z: ship.com.z + Math.sin(brg) * est, until: bf.time + 240 };
    }
  }

  /** Pass our sightings to consorts in signalling range. */
  private signal(bf: Battlefield): void {
    const me = this.vessel;
    const day = bf.daylight > 0.4;
    const reach = day ? Math.min(bf.visibility, 5000) : 7000;
    for (const c of this.contacts.values()) {
      if (!c.visible || c.secondHand) continue;
      const target = bf.vessels.find((v) => v.id === c.vessel);
      if (!target || !hostile(me, target) || this.signalled.has(c.vessel)) continue;
      this.signalled.add(c.vessel);
      if (this.difficulty.coordination <= 0) continue;
      bf.events.push({ type: 'signal', vessel: me.id, x: me.ship.com.x, y: 30, z: me.ship.com.z });
      for (const ally of bf.vessels) {
        if (ally === me || !ally.alive || ally.faction !== me.faction || !ally.captain) continue;
        if (Math.hypot(ally.ship.com.x - me.ship.com.x, ally.ship.com.z - me.ship.com.z) > reach) continue;
        ally.captain.receive({ ...c, secondHand: true }, this.difficulty.signalDelay * this.rng.range(0.7, 1.4));
      }
    }
  }

  receive(c: Contact, delay: number): void {
    this.pendingSignals.push({ t: delay, c });
  }

  /**
   * Dev: he knows exactly where `v` is, as if just reported, and (with
   * `target`) turns his mind to her now, without the usual reaction delay.
   */
  know(v: Vessel, time: number, target = true): void {
    const s = v.ship;
    this.contacts.set(v.id, {
      vessel: v.id, x: s.com.x, z: s.com.z, vx: s.vel.x, vz: s.vel.z, heading: s.heading,
      seen: time, awareness: 1.5, visible: true, secondHand: false,
    });
    if (!target) return;
    this.target = v.id;
    this.decisionTimer = 0;
    this.reactionTimer = 0;
  }

  // ------------------------------------------------------------------ decisions

  update(dt: number, bf: Battlefield): number {
    const me = this.vessel;
    const ship = me.ship;
    this.modeTime += dt;
    if (!me.fighting) {
      this.intent = me.state === 'struck' ? 'Colours struck' : me.state === 'sinking' ? 'Abandoning ship' : '';
      me.battery.standDown();
      return 0;
    }
    this.perceptionTimer -= dt;
    if (this.perceptionTimer <= 0) {
      this.perceptionTimer = 0.5;
      this.perceive(0.5, bf);
      this.signal(bf);
    }
    for (let i = this.pendingSignals.length - 1; i >= 0; i--) {
      const p = this.pendingSignals[i]!;
      p.t -= dt;
      if (p.t > 0) continue;
      this.pendingSignals.splice(i, 1);
      const mine = this.contacts.get(p.c.vessel);
      if (!mine || (!mine.visible && mine.seen < p.c.seen)) this.contacts.set(p.c.vessel, { ...p.c, awareness: mine?.awareness ?? 0 });
    }
    this.decisionTimer -= dt;
    this.reactionTimer -= dt;
    if (this.decisionTimer <= 0) {
      this.decisionTimer = this.difficulty.decisionInterval * this.rng.range(0.8, 1.2);
      this.decide(bf);
    }
    this.gunTimer -= dt;
    if (this.gunTimer <= 0) {
      this.gunTimer = 0.25;
      this.fightGuns(bf);
    }
    this.layGuns(dt, bf);
    // Helm: steer for the chosen heading, easing off as the swing builds.
    if (this.present && bf.time > this.present.until) this.present = null;
    let err = wrapAngle((this.present?.heading ?? this.desiredHeading) - ship.heading);
    // The short way round through the eye of the wind is a tack: it needs
    // way on her and a short swing, or she hangs in irons. Otherwise she
    // wears - the long way round, away from the wind.
    if (Math.abs(err) > 0.6) {
      let crosses = false;
      for (let k = 1; k <= 12 && !crosses; k++) crosses = Math.abs(wrapAngle(bf.wind.fromBearing - (ship.heading + (err * k) / 12))) < 0.45;
      const way = Math.hypot(ship.vel.x, ship.vel.z);
      if (crosses && (way < 3.2 || Math.abs(err) > 1.9)) err += err > 0 ? -Math.PI * 2 : Math.PI * 2;
    }
    const rate = -ship.angVel.y;
    return clamp(err * 3.2 - rate * 5, -1, 1);
  }

  private knownTarget(bf: Battlefield): { v: Vessel; c: Contact } | null {
    const me = this.vessel;
    let best: { v: Vessel; c: Contact } | null = null;
    let bs = -Infinity;
    for (const c of this.contacts.values()) {
      if (bf.time - c.seen > 120) continue;
      const v = bf.vessels.find((o) => o.id === c.vessel);
      if (!v || !v.alive || !hostile(me, v) || !v.fighting) continue;
      const d = Math.hypot(c.x - me.ship.com.x, c.z - me.ship.com.z);
      // Prefer the nearest; good captains pick on the most battered.
      let s = -d / 1000 + (c.visible ? 1 : 0) + (v.faction === 'player' ? 0.5 : 0);
      const flock = this.escort ? this.convoyCentre(bf) : null;
      if (flock) s += clamp(1 - Math.hypot(c.x - flock.x, c.z - flock.z) / 3000, 0, 1) * 2.5;
      s += (1 - v.damage.integrity) * 2 * this.difficulty.tactics;
      if (c.vessel === this.target) s += 0.8;
      if (s > bs) {
        bs = s;
        best = { v, c };
      }
    }
    return best;
  }

  private decide(bf: Battlefield): void {
    const me = this.vessel;
    const ship = me.ship;
    const pers = this.personality;
    const known = this.knownTarget(bf);
    const wasTarget = this.target;
    this.target = known ? known.v.id : -1;
    // How his people rate the fight (their morale settles toward it), and
    // whether they will go on with it at all.
    if (known) {
      const d = Math.hypot(known.c.x - ship.com.x, known.c.z - ship.com.z);
      const odds = power(me) / Math.max(0.1, this.enemyPower(bf, known.v));
      me.odds = odds;
      this.tendShip(bf, d);
      if (this.considerStriking(bf, known.v, d, odds)) return;
    } else {
      me.odds = 1;
      this.tendShip(bf, Infinity);
    }
    if (this.target !== wasTarget && this.target >= 0) this.reactionTimer = this.difficulty.reaction * this.rng.range(0.7, 1.3);
    // Reaction delay: keep doing what we were doing until it sinks in.
    if (this.reactionTimer > 0 && known && this.mode !== 'engage' && this.mode !== 'harry') return;

    const hull = me.damage.integrity;
    const crew = me.crew.strength;
    const mobility = ship.sails.sailHealth * ship.rudderHealth;
    let next: Mode = this.mode;
    if (known) {
      const { v, c } = known;
      const d = Math.hypot(c.x - ship.com.x, c.z - ship.com.z);
      const ratio = power(me) / Math.max(0.1, power(v) * this.rng.range(0.85, 1.15));
      const hurt = hull < pers.retreatHull || crew < pers.retreatCrew || ship.flooding > 0.5;
      const outgunned = (pers.runsFromStronger && ratio < 1.5) || ratio < 0.3 * (1 + pers.aggression);
      const theirMobility = v.ship.sails.sailHealth * v.ship.rudderHealth;
      // A raider doesn't run from a heavier ship: she harries it.
      const harry = pers.hitAndRun && (outgunned || ratio < 1.3);
      const flock = this.escort ? this.convoyCentre(bf) : null;
      if (me.faction === 'merchant') next = 'flee';
      else if (hurt || (outgunned && !harry)) next = this.allyNear(bf) && this.difficulty.coordination > 0.3 ? 'regroup' : 'flee';
      // An escort doesn't chase far from her charges.
      else if (flock && Math.hypot(flock.x - ship.com.x, flock.z - ship.com.z) > 2600 && Math.hypot(c.x - flock.x, c.z - flock.z) > 1600) next = 'regroup';
      else if (pers.boarding > 0.4 && d < 700 && (me.crew.effective > v.crew.effective * (1.6 - pers.boarding * 0.5) || theirMobility < 0.35)) next = 'board';
      else if (pers.ramming > 0.3 && d < 450 && ship.mass > v.ship.mass * 1.2 && theirMobility < 0.5) next = 'ram';
      else if (harry) next = d > 2200 ? 'pursue' : 'harry';
      else if (d > this.engageRange() * 1.3) next = 'pursue';
      else next = 'engage';
      // Stand by a battered consort.
      const hurtAlly = this.hurtAlly(bf);
      if (hurtAlly && next === 'engage' && pers.aggression < 0.8 && this.difficulty.coordination > 0.4) next = 'regroup';
      me.battle = d < 3200;
      if (mobility < 0.15 && next !== 'board') next = 'engage';
      if (this.forced) next = this.forced;
    } else if (this.poi && this.poi.until > bf.time) {
      next = Math.hypot(this.poi.x - ship.com.x, this.poi.z - ship.com.z) < 400 ? 'search' : 'investigate';
      me.battle = false;
    } else {
      next = 'patrol';
      me.battle = false;
    }
    if (next !== this.mode) {
      this.mode = next;
      this.modeTime = 0;
    }
    this.chooseHeading(bf, known);
    this.chooseSail(bf, known);
    if (known) {
      const rel = wrapAngle(Math.atan2(known.c.z - ship.com.z, known.c.x - ship.com.x) - Math.atan2(bearingToZ(ship.heading), bearingToX(ship.heading)));
      // Enemy on our starboard side when rel is positive (clockwise from bow).
      me.threatSide = rel > 0 ? 1 : -1;
      me.threatRange = Math.hypot(known.c.x - ship.com.x, known.c.z - ship.com.z);
    } else {
      me.threatSide = 0;
      me.threatRange = Infinity;
    }
  }

  /** The middle of the convoy she sails with (null if none left afloat). */
  private convoyCentre(bf: Battlefield): { x: number; z: number } | null {
    let x = 0, z = 0, n = 0;
    for (const id of this.convoy) {
      const v = bf.vessels.find((o) => o.id === id);
      if (!v || v === this.vessel || !v.alive || !v.fighting) continue;
      x += v.ship.com.x;
      z += v.ship.com.z;
      n++;
    }
    return n ? { x: x / n, z: z / n } : null;
  }

  /** The power of the enemy he faces: her, and her consorts near her. */
  private enemyPower(bf: Battlefield, v: Vessel): number {
    let p = power(v);
    for (const o of bf.vessels) {
      if (o === v || o === this.vessel || !o.fighting || !(hostile(this.vessel, o) || hostile(o, this.vessel))) continue;
      if (Math.hypot(o.ship.com.x - v.ship.com.x, o.ship.com.z - v.ship.com.z) < 900) p += power(o) * 0.7;
    }
    return p;
  }

  /**
   * Strike, if it has come to that: a merchantman run down by an armed ship
   * (she can't get away and is being fired on, or soon will be) heaves to
   * rather than be shot to pieces; a man-of-war or a pirate gives up only
   * when her people have lost heart (pirates last, the gallows waiting) and
   * she is beaten - shot through, crippled aloft, short of men, flooding -
   * or hopelessly outgunned with no way out.
   */
  /**
   * What he orders as his ship is hurt: hands to the pumps and the fires
   * when it's serious, to the canvas and spare spars once out of the thick
   * of it, and grog when his people waver in a fight.
   */
  private tendShip(bf: Battlefield, d: number): void {
    const me = this.vessel, ship = me.ship;
    if (!me.fighting) return;
    me.order('leaks', ship.flooding > 0.22 || me.damage.leakArea > 0.25);
    me.order('fire', me.fire.worst > 0.3);
    const quiet = d > 1200;
    me.order('canvas', quiet && ship.sails.sailHealth < 0.85);
    me.order('spars', quiet && Object.values(me.damage.masts).some((m) => m.lostLevel > 0 || m.rigging < 0.6));
    // (One cask in a fight: a captain keeps the rest.)
    if (d < 900 && me.faction !== 'merchant' && me.grogCasks >= 3 && me.crew.morale < this.personality.strikeMorale + 0.07) me.rally(bf.events);
  }

  private considerStriking(bf: Battlefield, v: Vessel, d: number, odds: number): boolean {
    const me = this.vessel, ship = me.ship, pers = this.personality;
    if (!me.fighting || me.faction === 'player') return false;
    const morale = me.crew.morale;
    const mobility = ship.sails.sailHealth * ship.rudderHealth;
    const ours = Math.hypot(ship.vel.x, ship.vel.z), theirs = Math.hypot(v.ship.vel.x, v.ship.vel.z);
    const cornered = mobility < 0.35 || ship.sails.masts.length < me.cls.masts.length || (d < 600 && theirs > ours * 0.95);
    const underFire = me.clock - me.lastHitAt < 45;
    const armed = v.battery.guns.some((g) => !g.destroyed);
    let strike = false;
    if (me.faction === 'merchant') {
      // (After a try at getting away: she runs first.)
      strike = armed && cornered && this.modeTime > 8 && (d < 220 || (d < 700 && underFire)) && morale < pers.strikeMorale + 0.2;
    } else {
      const beaten = me.damage.integrity < 0.5 || mobility < 0.3 || me.crew.strength < 0.4 || ship.flooding > 0.45;
      const hopeless = odds < 0.45 && cornered && d < 700 && underFire;
      strike = morale < pers.strikeMorale && (beaten || hopeless);
    }
    if (!strike) return false;
    me.strike(bf.events);
    this.intent = 'Colours struck';
    this.laying = null;
    me.battery.standDown();
    return true;
  }

  private engageRange(): number {
    return Math.min(AMMO[this.vessel.battery.ammo].range * 0.8, this.personality.range * 1.8 + 150);
  }

  private allyNear(bf: Battlefield): Vessel | null {
    const me = this.vessel;
    let best: Vessel | null = null, bd = 6000;
    for (const v of bf.vessels) {
      if (v === me || !v.alive || (v.faction !== me.faction && !this.convoy.includes(v.id)) || !v.fighting) continue;
      const d = Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z);
      if (d < bd) { bd = d; best = v; }
    }
    return best;
  }

  private hurtAlly(bf: Battlefield): Vessel | null {
    const me = this.vessel;
    for (const v of bf.vessels) {
      if (v === me || !v.alive || v.faction !== me.faction || !v.fighting || !v.battle) continue;
      if (v.damage.integrity < 0.45 && Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z) < 2500) return v;
    }
    return null;
  }

  /** Score candidate headings over a short look-ahead and pick the best. */
  private chooseHeading(bf: Battlefield, known: { v: Vessel; c: Contact } | null): void {
    const me = this.vessel;
    const ship = me.ship;
    const pers = this.personality;
    const diff = this.difficulty;
    const windFrom = bf.wind.fromBearing;
    const T = diff.horizon;
    const vBest = Math.max(2, 4.8 * Math.sqrt(ship.sx) * (0.4 + 0.6 * ship.sails.sailHealth));
    const px = ship.com.x, pz = ship.com.z;
    const tgt = known?.c;
    const tv = known?.v;
    const tx = tgt ? tgt.x + tgt.vx * T : 0, tz = tgt ? tgt.z + tgt.vz * T : 0;
    // Flanking slot around the target, shared with consorts.
    let slot: { x: number; z: number } | null = null;
    if (tgt && tv && diff.coordination > 0.2 && (this.mode === 'engage' || this.mode === 'pursue')) {
      const list = bf.attackers.get(tv.id) ?? [];
      const k = list.indexOf(me.id);
      if (list.length > 1 && k >= 0) {
        const slots = [2.1, -2.1, 1.0, -1.0, Math.PI];
        const a = Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)) + slots[k % slots.length]!;
        slot = { x: tx + Math.cos(a) * pers.range, z: tz + Math.sin(a) * pers.range };
      }
    }
    let intercept: { x: number; z: number } | null = null;
    if (tgt && (this.mode === 'pursue' || this.mode === 'ram' || this.mode === 'board')) {
      // Where can we meet her, given our speed and her course?
      let t = Math.hypot(tgt.x - px, tgt.z - pz) / vBest;
      for (let i = 0; i < 4; i++) t = Math.hypot(tgt.x + tgt.vx * t - px, tgt.z + tgt.vz * t - pz) / vBest;
      intercept = { x: tgt.x + tgt.vx * t, z: tgt.z + tgt.vz * t };
    }
    const ally = this.mode === 'regroup' ? this.allyNear(bf) ?? this.hurtAlly(bf) : null;

    let best = ship.heading, bs = -Infinity;
    const n = diff.headings;
    for (let i = 0; i < n; i++) {
      const h = (i / n) * Math.PI * 2 + this.rng.range(-0.05, 0.05);
      const windAngle = wrapAngle(windFrom - h);
      const v = vBest * polar(windAngle);
      const fx = bearingToX(h), fz = bearingToZ(h);
      // The first few seconds she carries on as she was.
      const qx = px + ship.vel.x * 4 + fx * v * (T - 4);
      const qz = pz + ship.vel.z * 4 + fz * v * (T - 4);
      let s = 0;
      if (Math.abs(windAngle) < 0.95) s -= 60;
      s -= Math.abs(wrapAngle(h - ship.heading)) * 0.35;
      switch (this.mode) {
        case 'patrol': {
          const w = this.route[this.wp % Math.max(1, this.route.length)];
          if (w) {
            if (Math.hypot(w.x - px, w.z - pz) < 400) this.wp++;
            s -= Math.hypot(w.x - qx, w.z - qz) / 200;
          } else s -= Math.abs(wrapAngle(h - ship.heading)) * 2;
          break;
        }
        case 'investigate':
        case 'search': {
          const p = this.poi!;
          const jitter = this.mode === 'search' ? 250 : 0;
          s -= Math.hypot(p.x + Math.cos(this.modeTime * 0.05) * jitter - qx, p.z + Math.sin(this.modeTime * 0.05) * jitter - qz) / 200;
          break;
        }
        case 'engage': {
          if (!tgt || !tv) break;
          const d = Math.hypot(tx - qx, tz - qz);
          s -= Math.pow((d - pers.range) / pers.range, 2) * 6;
          // Guns bear on the beam: we want her abeam of us.
          const rel = wrapAngle(Math.atan2(tz - qz, tx - qx) - Math.atan2(fz, fx));
          const off = Math.abs(Math.abs(rel) - Math.PI / 2) / (Math.PI / 2);
          const side = rel > 0 ? 1 : -1;
          const loaded = me.battery.ready(side as 1 | -1) / Math.max(1, me.battery.count(side as 1 | -1));
          s += (1 - off * off) * (4 + loaded * 3) * pers.beam;
          // With guns loaded and in range, bring them to bear now: turn
          // her across the line of fire rather than wait for the geometry.
          const relNow = wrapAngle(Math.atan2(tgt.z - pz, tgt.x - px) - Math.atan2(fz, fx));
          const offNow = Math.abs(Math.abs(relNow) - Math.PI / 2) / (Math.PI / 2);
          const sideNow = (relNow > 0 ? 1 : -1) as 1 | -1;
          const readyNow = me.battery.ready(sideNow) / Math.max(1, me.battery.count(sideNow));
          const dNow = Math.hypot(tgt.x - px, tgt.z - pz);
          if (dNow < AMMO[me.battery.ammo].range) s += (1 - offNow * offNow) * readyNow * 7 * (0.5 + 0.5 * diff.tactics);
          // Stay out of her broadside, and cross her bow or stern to rake.
          const relT = wrapAngle(Math.atan2(qz - tz, qx - tx) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
          const inHerArc = 1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2);
          const theirGuns = tv.battery.ready(relT > 0 ? 1 : -1) / Math.max(1, tv.battery.count(relT > 0 ? 1 : -1));
          s -= inHerArc * inHerArc * theirGuns * 5 * (1 - pers.aggression * 0.6) * (0.4 + diff.tactics) * pers.wary;
          const rake = Math.max(0, Math.cos(relT) * Math.cos(relT) - 0.7) * 3;
          s += rake * 6 * diff.tactics * (d < pers.range * 1.5 ? 1 : 0.3);
          // Weather gauge: upwind of her gives us the choice of range.
          const upwind = (qx - tx) * -bearingToX(windFrom) + (qz - tz) * -bearingToZ(windFrom);
          s += clamp(-upwind / 400, -1, 1) * 0.8 * pers.aggression * diff.tactics;
          if (slot) s -= (Math.hypot(slot.x - qx, slot.z - qz) / pers.range) * 3 * diff.coordination;
          // An escort keeps between the enemy and her charges.
          const flock = this.escort ? this.convoyCentre(bf) : null;
          if (flock) s -= Math.hypot((tx + flock.x) / 2 - qx, (tz + flock.z) / 2 - qz) / 400;
          if (d < 60) s -= 20;
          break;
        }
        case 'harry': {
          // In across her bow or stern with a loaded broadside, rake her, and
          // away out of reach to reload; never lie in her arcs.
          if (!tgt || !tv) break;
          const loadedBest = Math.max(me.battery.ready(1) / Math.max(1, me.battery.count(1)), me.battery.ready(-1) / Math.max(1, me.battery.count(-1)));
          const attacking = loadedBest >= 0.6;
          const d = Math.hypot(tx - qx, tz - qz);
          const want = attacking ? pers.range : Math.min(AMMO[tv.battery.ammo].range * 0.85, 900);
          s -= Math.pow((d - want) / want, 2) * (attacking ? 1.5 : 5);
          const relT = wrapAngle(Math.atan2(qz - tz, qx - tx) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
          const inHerArc = 1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2);
          const theirGuns = tv.battery.ready(relT > 0 ? 1 : -1) / Math.max(1, tv.battery.count(relT > 0 ? 1 : -1));
          // Close to her, her broadside is death; far off it matters less.
          s -= inHerArc * inHerArc * (0.25 + theirGuns) * 6 * pers.wary * clamp(1.4 - d / 900, 0.2, 1.2);
          if (attacking) {
            // For a point off her bow or stern (the nearer), to cross it and rake her...
            const hx = bearingToX(tgt.heading), hz = bearingToZ(tgt.heading);
            const end = Math.hypot(tx + hx * pers.range - px, tz + hz * pers.range - pz) < Math.hypot(tx - hx * pers.range - px, tz - hz * pers.range - pz) ? 1 : -1;
            s -= Math.hypot(tx + hx * pers.range * end - qx, tz + hz * pers.range * end - qz) / 45;
            const rake = Math.max(0, Math.cos(relT) * Math.cos(relT) - 0.6) * 2.5;
            s += rake * 9 * (0.5 + diff.tactics);
            // ...and once there, her abeam of us, to fire.
            const rel = wrapAngle(Math.atan2(tz - qz, tx - qx) - Math.atan2(fz, fx));
            const off = Math.abs(Math.abs(rel) - Math.PI / 2) / (Math.PI / 2);
            s += (1 - off * off) * 3 * pers.beam * (d < pers.range * 2 ? 1 : 0.3);
          } else {
            // Off to reload: at speed, and to windward of her, where a
            // small fore-and-aft rig out-points any square-rigger.
            s += v * 0.6;
            const upwind = (qx - tx) * -bearingToX(windFrom) + (qz - tz) * -bearingToZ(windFrom);
            s += clamp(-upwind / 300, -1, 1) * 1.8;
          }
          if (d < 50) s -= 25;
          break;
        }
        case 'pursue':
        case 'ram':
        case 'board': {
          if (!intercept || !tgt) break;
          const aim = this.mode === 'board' && tv
            ? { x: tgt.x + tgt.vx * 6 - bearingToX(tgt.heading) * 25, z: tgt.z + tgt.vz * 6 - bearingToZ(tgt.heading) * 25 }
            : intercept;
          s -= Math.hypot(aim.x - qx, aim.z - qz) / 60;
          if (this.mode === 'board' && tv) {
            // Come in on her quarter, not into her broadside.
            const relT = wrapAngle(Math.atan2(qz - tgt.z, qx - tgt.x) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
            s -= (1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2)) * 2;
          }
          if (slot) s -= (Math.hypot(slot.x - qx, slot.z - qz) / 800) * diff.coordination;
          break;
        }
        case 'flee': {
          if (tgt) {
            const d = Math.hypot(tgt.x + tgt.vx * T - qx, tgt.z + tgt.vz * T - qz);
            s += d / 150 + v * 2;
            // Don't run across her broadside.
            const relT = wrapAngle(Math.atan2(qz - tgt.z, qx - tgt.x) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
            s -= (1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2)) * 1.5;
          } else s += v * 3;
          const a = this.allyNear(bf);
          if (a) s -= Math.hypot(a.ship.com.x - qx, a.ship.com.z - qz) / 900;
          break;
        }
        case 'regroup': {
          const flock = this.escort ? this.convoyCentre(bf) : null;
          if (flock) {
            s -= Math.hypot(flock.x - qx, flock.z - qz) / 150;
            break;
          }
          if (ally) {
            // Between the hurt consort and the enemy, or alongside a friend.
            const gx = tgt ? (ally.ship.com.x * 2 + tgt.x) / 3 : ally.ship.com.x;
            const gz = tgt ? (ally.ship.com.z * 2 + tgt.z) / 3 : ally.ship.com.z;
            s -= Math.hypot(gx - qx, gz - qz) / 150;
          }
          break;
        }
        default: break;
      }
      // Keep off other ships (unless we mean to be alongside).
      for (const o of bf.vessels) {
        if (o === me || !o.alive) continue;
        if ((this.mode === 'ram' || this.mode === 'board') && o.id === this.target) continue;
        const ox = o.ship.com.x + o.ship.vel.x * T, oz = o.ship.com.z + o.ship.vel.z * T;
        const d = Math.hypot(ox - qx, oz - qz);
        if (d < 90) s -= (90 - d) / 12;
      }
      s += this.rng.gauss() * diff.noise * 2;
      if (s > bs) {
        bs = s;
        best = h;
      }
    }
    this.desiredHeading = best;
    this.intent = this.describe(known);
  }

  private describe(known: { v: Vessel; c: Contact } | null): string {
    const name = known ? known.v.name : '';
    switch (this.mode) {
      case 'patrol': return 'Patrolling';
      case 'investigate': return 'Investigating gunfire';
      case 'search': return 'Searching';
      case 'engage': return this.escort ? `Defending the convoy from ${name}` : `Engaging ${name}`;
      case 'harry': return `Harrying ${name}`;
      case 'pursue': return `Chasing ${name}`;
      case 'flee': return 'Running';
      case 'regroup': return 'Closing on consort';
      case 'board': return `Closing to board ${name}`;
      case 'ram': return `Bearing down to ram ${name}`;
      default: return '';
    }
  }

  private chooseSail(bf: Battlefield, known: { v: Vessel; c: Contact } | null): void {
    const me = this.vessel;
    const rig = me.ship.sails;
    const wind = bf.wind.speed;
    let plan = 3;
    switch (this.mode) {
      case 'engage': plan = 2; break;
      case 'harry': plan = 3; break;
      case 'pursue': case 'flee': case 'ram': plan = 4; break;
      case 'board': {
        const d = known ? Math.hypot(known.c.x - me.ship.com.x, known.c.z - me.ship.com.z) : 999;
        plan = d < 120 ? 1 : d < 400 ? 2 : 4;
        break;
      }
      case 'regroup': plan = 3; break;
      default: plan = 3;
    }
    // Shorten sail for the weather, and a prudent captain more so.
    if (wind > 17) plan = Math.min(plan, 1);
    else if (wind > 13) plan = Math.min(plan, 2);
    if (plan !== rig.plan) rig.setPlan(plan);
  }

  // ------------------------------------------------------------------ gunnery

  private fightGuns(bf: Battlefield): void {
    const me = this.vessel;
    const ship = me.ship;
    const battery = me.battery;
    const tgtId = this.target;
    if (tgtId < 0 || this.forced === 'idle') {
      me.gunSide = 0;
      this.laying = null;
      return;
    }
    const c = this.contacts.get(tgtId);
    const tv = bf.vessels.find((v) => v.id === tgtId);
    this.laying = null;
    if (!c || !tv || !tv.fighting || bf.time - c.seen > 6) return;
    const d = Math.hypot(c.x - ship.com.x, c.z - ship.com.z);

    // Choose what to load for the next rounds.
    this.chooseAmmo(d, tv);
    const ammo = AMMO[battery.ammo];
    const local = ship.worldToLocal(tmp.set(c.x, tv.ship.com.y, c.z), tmp);
    const side = (local.z > 0 ? 1 : -1) as 1 | -1;
    me.gunSide = side;
    if (d > ammo.range * (0.85 + 0.25 * this.personality.aggression)) return;
    // Is she on the beam, within the guns' arc?
    // The broadside cycle: with a side loaded and in range, put the helm over
    // to lay her on that beam; once it's fired she manoeuvres while reloading.
    // (Harrying, only once she's in close: from afar she comes on to rake, not to trade broadsides.)
    const close = this.mode !== 'harry' || d < this.personality.range * 2.4;
    if (close && (this.mode === 'engage' || this.mode === 'harry' || this.mode === 'pursue' || this.mode === 'regroup' || this.mode === 'flee')) {
      const bearing = Math.atan2(c.x - ship.com.x, -(c.z - ship.com.z));
      let bestH = 0, bestS = -Infinity, bestSide: 1 | -1 = 1;
      for (const s of [1, -1] as const) {
        const frac = battery.ready(s) / Math.max(1, battery.count(s));
        if (frac < 0.6) continue;
        const h = wrapAngle(bearing - s * Math.PI / 2);
        const dh = wrapAngle(h - ship.heading);
        const turn = Math.abs(dh);
        // A short swing only (running ships even less), and never one that
        // would take her through the eye of the wind.
        if (turn > ((this.mode === 'flee' || this.mode === 'regroup') ? 0.5 : 1.3)) continue;
        let crosses = false;
        for (let k = 0; k <= 8 && !crosses; k++) crosses = Math.abs(wrapAngle(bf.wind.fromBearing - (ship.heading + (dh * k) / 8))) < 0.95;
        if (crosses) continue;
        const score = frac * 2 - turn;
        if (score > bestS) {
          bestS = score;
          bestH = h;
          bestSide = s;
        }
      }
      if (bestS > -5 && d < ammo.range * (0.9 + 0.3 * this.personality.aggression)) this.present = { heading: bestH, until: bf.time + 1, side: bestSide };
    }
    // Lay the guns on her (layGuns, every step, as the player's are laid):
    // where she'll be when the shot gets there, as near as he can judge the
    // range; the waterline to sink her, her rigging to cripple her, her deck
    // to clear it.
    // Harrying, she holds her fire for the rake: nothing from afar.
    if (this.mode === 'harry' && d > this.personality.range * 2.6) return;
    const part: AimPart = ammo.id === 'chain' ? 'rigging' : ammo.id === 'grape' || ammo.id === 'shell' ? 'hull' : 'waterline';
    if (this.rangeOf !== tgtId) {
      this.rangeOf = tgtId;
      this.rangeErr = this.rng.gauss() * this.rangeSigma();
    }
    this.laying = { vessel: tgtId, side, height: aimHeight(part, tv.ship.sy) };
    // Fire when the guns are on her: each gun captain then waits for the roll.
    const aim = battery.aim;
    if (!aim || aim.side !== side || aim.status !== 'ok' || aim.bearing === 0) return;
    const loaded = battery.ready(side), total = battery.count(side);
    if (loaded === 0 || aim.laid < Math.max(1, aim.bearing * 0.6)) return;
    let fired = 0;
    if (this.rng.chance(this.personality.broadsides)) {
      if (loaded >= total * 0.7 || (loaded > 0 && d < 150)) {
        fired = battery.fire(side);
        this.present = null;
      }
    } else {
      battery.guns.forEach((g, i) => {
        if (g.spec.side === side && g.loaded && g.laid && this.rng.chance(0.5) && battery.fireGun(i)) fired++;
      });
    }
    // He judges the range better for seeing where that lot fell.
    if (fired > 0) this.rangeErr = this.rangeErr * 0.45 + this.rng.gauss() * this.rangeSigma() * 0.5;
  }

  /** How far out his judgement of the range is, one sigma, as a fraction of it. */
  private rangeSigma(): number {
    return 0.008 + 0.03 * (1 - this.vessel.crew.gunnery);
  }

  /**
   * Keep the guns laid on the ship he's fighting (or stand them down): the
   * same laying as the player's - the port's traverse, the quoin's reach,
   * the crews' pace and the roll all bind him too. She's led by her way over
   * the ground and the guns' own time of flight (the shot carries ours).
   */
  private layGuns(dt: number, bf: Battlefield): void {
    const me = this.vessel, battery = me.battery, ship = me.ship;
    const l = this.laying;
    const c = l ? this.contacts.get(l.vessel) : undefined;
    if (!l || !c || me.gunSide !== l.side) {
      this.laying = null;
      battery.standDown();
      return;
    }
    const ago = Math.max(0, bf.time - c.seen);
    let x = c.x + c.vx * ago, z = c.z + c.vz * ago;
    const prior = battery.aim;
    const tof = prior && prior.tof > 0 ? prior.tof : Math.hypot(x - ship.com.x, z - ship.com.z) / 420;
    x += c.vx * tof;
    z += c.vz * tof;
    // Over or short by his misjudging the range.
    const dx = x - ship.com.x, dz = z - ship.com.z;
    x += dx * this.rangeErr;
    z += dz * this.rangeErr;
    battery.layOn(l.side, x, z, dt, ship, bf.wind, l.height, FLIGHT_DT);
  }

  private chooseAmmo(d: number, tv: Vessel): void {
    const me = this.vessel;
    const carried = me.cls.ammo;
    const pers = this.personality;
    let want: AmmoId = 'round';
    const theirRig = tv.ship.sails.sailHealth;
    if (carried.includes('grape') && d < 160 && (this.mode === 'board' || pers.aggression > 0.6)) want = 'grape';
    else if (carried.includes('chain') && d < 480 && theirRig > 0.4 && (pers.cripple || (this.mode === 'pursue' && this.difficulty.tactics > 0.4))) want = 'chain';
    else if (carried.includes('shell') && d < 900 && d > 250 && this.difficulty.tactics > 0.6 && tv.ship.sx > 0.9 && this.rng.chance(0.3)) want = 'shell';
    me.battery.ammo = want;
  }
}

const tmp = new Vec3();
